<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useHelpers, useTranslate } from '@/shared/lib'
import {
  buildYandexMapsPointUrl,
  coordinatesToLngLat,
} from '@/shared/lib/yandexMaps/buildYandexMapsPointUrl'
import { useNotification, VIcons, VInventAccentStatus, VPreloader } from '@/shared/ui'
import { useRoute, useRouter } from 'vue-router'
import { WObjectCoordsManuallyChangeForm } from '@/widgets'
import { centerMean, points } from '@turf/turf'
import { useGlobalSearch } from '@/widgets/GlobalSearch'
import { usePassportRuntimeV2 } from '@/features/Inventory/PassportRuntimeV2'
import { useAuth } from '@/entities/Auth'
import { useQueryClient } from '@tanstack/vue-query'
import {
  useLoadInventObjectDetail,
  useLoadInventObjectGeoJson,
  useSaveInventObjectDetail,
} from '../model/api.ts'
import type { InventoryMapInfoHeader } from '../model/types.ts'
import WInventoryMapInfoItemFiles from './WInventoryMapInfoItemFiles.vue'

const props = defineProps<{
  header: InventoryMapInfoHeader
  /** Identity панели для родительского `openPanels` (id объекта). */
  value: number
  /** Раскрыта ли шапка сейчас — гейт для ленивой подгрузки. */
  isOpen: boolean
}>()

const emits = defineEmits<{
  updateSelected: [ids: number[]]
  updateCoords: []
  deleteFromSelected: [id: number]
  mapObjectFocused: [coordinates: number[][]]
}>()

const route = useRoute()
const router = useRouter()
const { trs } = useTranslate()
const { addWarning } = useNotification()
const { isPermissions, hasElementPermission } = useAuth()
const queryClient = useQueryClient()

const objectId = computed(() => props.header.id)
const isOpenRef = computed(() => props.isOpen)

const { data: detail, isFetching: isDetailFetching } = useLoadInventObjectDetail(
  objectId,
  isOpenRef,
)
const isLoading = computed(() => isDetailFetching.value)

/** Выгрузка координат объекта в GeoJSON */
const { load: loadGeoJson, isPending: isExportingCoords } = useLoadInventObjectGeoJson()
const { saveFile } = useHelpers()
const exportCoordsHandler = async () => {
  const blob = await loadGeoJson(props.header.id)
  if (!blob) return
  saveFile(URL.createObjectURL(blob), `geo_json_${props.header.id}.json`)
}

/** Локальные поля формы — имя/описание, единственное, что реально редактируется
 *  в новой карточке объекта (`PATCH /objects/{id}`). */
const formName = ref('')
const formDescription = ref('')
watch(detail, (value) => {
  if (!value) return
  formName.value = value.name
  formDescription.value = value.description ?? ''
})

const isDirty = computed(() => {
  if (!detail.value) return false
  return (
    formName.value !== detail.value.name ||
    formDescription.value !== (detail.value.description ?? '')
  )
})

const canEditObject = computed(
  () =>
    isPermissions(['OBJECT_EDIT']) &&
    (!detail.value || hasElementPermission(detail.value.type_id, 'update')),
)

const { save: saveDetail, isPending: isSaving } = useSaveInventObjectDetail()

/** Сохраняет имя/описание и обновляет шапку на карте (там всё ещё старое имя). */
const saveHandler = async () => {
  if (!isDirty.value) return

  const saved = await saveDetail({
    id: props.header.id,
    name: formName.value,
    description: formDescription.value,
  })
  if (!saved) return

  await queryClient.invalidateQueries({ queryKey: ['invent_map_info_detail', props.header.id] })
  emits('updateCoords')
}

/**
 * Сводит геометрию объекта (Point: `[lon,lat]`, Line/Polygon: вложенные массивы)
 * к плоскому списку точек `[lon,lat][]` — для фокуса карты и поиска по координатам.
 * @param coords
 */
function flattenCoordinatePairs(coords: unknown): number[][] {
  if (Array.isArray(coords) && typeof coords[0] === 'number') return [coords as number[]]
  if (Array.isArray(coords)) return (coords as unknown[]).flatMap(flattenCoordinatePairs)
  return []
}

/** Функция центрирования карты по координатам объекта */
const showInMapHandler = () => {
  emits('mapObjectFocused', flattenCoordinatePairs(props.header.coordinates))
}

/** Открыть объект на Яндекс.Картах в новой вкладке */
const openInNewTabHandler = () => {
  const coords = coordinatesToLngLat(props.header.coordinates as number[][])
  if (!coords) {
    addWarning(trs('itf.passport_runtime_list_map_disabled_no_geom'))
    return
  }
  window.open(buildYandexMapsPointUrl(coords.lng, coords.lat), '_blank', 'noopener,noreferrer')
}

/** Открываем поиск с данными по координатам */
const searchModel = useGlobalSearch()
const searchWithCoordsHandler = () => {
  const coordsCenter = centerMean(points(flattenCoordinatePairs(props.header.coordinates)))

  searchModel.query.area = {
    type: 'circle',
    center: coordsCenter.geometry.coordinates,
    radius: 1000,
  }
  searchModel.open = true
}

const controlClearBtn = computed(() => ({
  icon: 'mop',
  text: 'itf.clear_selected',
  color: 'error',
}))

/** Функция удаления элемента из списка выделенных */
const deleteHandler = (event: MouseEvent) => {
  if (event.shiftKey) {
    emits('updateSelected', [props.header.id])
  } else {
    emits('deleteFromSelected', props.header.id)
  }
}

/** Ссылка на историю изменений */
const historyLink = computed(() =>
  router.resolve({
    name: 'object.inventory.history',
    params: { ...route.params, tab: 'inventory', tab_inventory: 'history' },
  }),
)

/** Ссылка на паспорт объекта (runtime v2) */
const getLinkToPassportV2 = (id: number) =>
  router.resolve({
    name: 'object.inventory.elements.passport-v2',
    params: {
      type: route.params.type,
      object_id: route.params.object_id,
      tab: 'inventory',
      tab_inventory: 'elements',
      invent_object_id: String(id),
    },
  })

const passportModel = usePassportRuntimeV2()
</script>

<template>
  <VExpansionPanel :value="value" density="compact" :min-width="300" :max-width="500">
    <template #title>
      <div class="w-invent-map-info-item__header">
        <div class="w-invent-map-info-item__title">
          <div class="w-invent-map-info-item__title-row">
            <VIcons :type="header.icon" class="w-invent-map-info-item__icon_active" />

            <div class="w-invent-map-info-item__name">{{ header.name }}</div>
          </div>

          <div v-if="header.typeName" class="w-invent-map-info-item__type-name">
            {{ header.typeName }}
          </div>
        </div>

        <div class="w-invent-map-info-item__links">
          <VTooltip location="top" :open-delay="1000" :text="$t('itf.map')">
            <template #activator="{ props: activatorProps }">
              <VBtn
                icon
                density="compact"
                variant="text"
                v-bind="activatorProps"
                @click.stop="showInMapHandler"
              >
                <VIcons type="location_on" />
              </VBtn>
            </template>
          </VTooltip>

          <VTooltip location="top" :open-delay="1000" :text="$t('itf.open_in_new_tab')">
            <template #activator="{ props: activatorProps }">
              <VBtn
                icon
                density="compact"
                variant="text"
                v-bind="activatorProps"
                @click.stop="openInNewTabHandler"
              >
                <VIcons type="open_in_new" />
              </VBtn>
            </template>
          </VTooltip>

          <VTooltip location="top" :open-delay="1000" :text="$t('itf.search_to_coords')">
            <template #activator="{ props: activatorProps }">
              <VBtn
                icon
                density="compact"
                variant="text"
                color="default"
                v-bind="activatorProps"
                @click.stop="searchWithCoordsHandler"
              >
                <VIcons type="search" />
              </VBtn>
            </template>
          </VTooltip>

          <VTooltip location="top" :open-delay="1000" :text="$t(controlClearBtn.text)">
            <template #activator="{ props: activatorProps }">
              <VBtn
                icon
                density="compact"
                :color="controlClearBtn.color"
                variant="plain"
                v-bind="activatorProps"
                @mousedown.stop="deleteHandler"
              >
                <VIcons :type="controlClearBtn.icon" />
              </VBtn>
            </template>
          </VTooltip>
        </div>
      </div>
    </template>

    <template #text>
      <VPreloader v-if="isLoading" :wrapper-height="120" />

      <template v-else>
        <div class="w-invent-map-info-item__sub-header">
          <div class="w-invent-map-info-item__id">Ид.: {{ header.id }}</div>

          <div class="w-invent-map-info-item__bts">
            <VInventAccentStatus v-if="detail?.acceptStatus" :status="detail.acceptStatus" />

            <VBtn :to="historyLink" density="compact">Ист. изм.</VBtn>

            <VBtn
              color="warning"
              density="compact"
              :loading="isExportingCoords"
              @click="exportCoordsHandler"
            >
              {{ $t('itf.map_export_coords') }}
            </VBtn>
          </div>
        </div>

        <VDivider class="mt-4 mb-4" />

        <VTextField
          v-model="formName"
          :label="$t('itf.name')"
          :disabled="!canEditObject"
          density="compact"
          class="mb-2"
        />

        <VTextarea
          v-model="formDescription"
          :label="$t('itf.description')"
          :disabled="!canEditObject"
          density="compact"
          rows="2"
          auto-grow
          class="mb-2"
        />

        <WInventoryMapInfoItemFiles
          :object-id="header.id"
          :enabled="isOpen"
          :can-edit="canEditObject"
          class="mb-4"
        />

        <WObjectCoordsManuallyChangeForm :object-id="header.id" @update="emits('updateCoords')" />

        <div class="form__btn_between w-invent-map-info-item__actions">
          <VBtn color="primary" :loading="isSaving" :disabled="!canEditObject" @click="saveHandler">
            {{ $t('itf.save') }}
            <span v-if="isDirty" class="w-invent-map-info-item__dirty-dot" />
          </VBtn>

          <div class="w-invent-map-info-item__bts">
            <VTooltip location="top" :open-delay="1000" :text="$t('itf.map_open_passport_modal')">
              <template #activator="{ props: activatorProps }">
                <VBtn
                  variant="outlined"
                  @click="passportModel.openHandler(header.id)"
                  v-bind="activatorProps"
                >
                  <VIcons type="list_alt" />
                </VBtn>
              </template>
            </VTooltip>

            <VTooltip location="top" :open-delay="1000" :text="$t('itf.map_open_passport')">
              <template #activator="{ props: activatorProps }">
                <VBtn
                  variant="outlined"
                  :to="getLinkToPassportV2(header.id)"
                  v-bind="activatorProps"
                >
                  <VIcons type="format_list_bulleted" />
                </VBtn>
              </template>
            </VTooltip>
          </div>
        </div>
      </template>
    </template>
  </VExpansionPanel>
</template>
